'use client';

import React from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { LazyLoadImage } from '@depop/web-ui-kit/LazyLoadImage';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { Price } from '@/components/Price';
import { ProductCard } from '@/components/ProductCard';
import { ProductCardLike } from '@/components/ProductCard/ProductCardLike';
import { SecondaryPriceLine } from '@/components/SecondaryPriceLine';
import { type CurrencyCode } from '@/modules/currencies/constants';
import { type GuestLikeProductInput } from '@/modules/guestLikes/types';
import { buildProductImageProps } from '@/modules/pictures/helpers.ts';
import { type DisplayPrice } from '@/modules/price/aliases';
import { type MoreItemsProduct } from '@/modules/product/aliases.ts';
import { getSizeString } from '@/modules/product/helpers/getSizeString';
import { type Preview } from '@/modules/product/types.ts';

type Props = {
  id: MoreItemsProduct['id'];
  slug: MoreItemsProduct['slug'];
  pricing: MoreItemsProduct['pricing'];
  displayPrice: DisplayPrice;
  sizes: MoreItemsProduct['sizes'];
  preview: Preview;
  isLiked: MoreItemsProduct['is_liked'];
  brandName?: MoreItemsProduct['brand_name'];
  hoverPicture?: Preview;
  sellerUsername: string;
  handleClick: () => void;
  guestLikeProduct: GuestLikeProductInput;
};

export function MoreSellerItemsCard({
  id,
  slug,
  displayPrice,
  sizes,
  preview,
  isLiked,
  brandName,
  hoverPicture,
  sellerUsername,
  handleClick,
  guestLikeProduct,
}: Props) {
  const tCommon = useTranslations('common');
  const sizeString = getSizeString({
    sizes,
    multipleSizesString: tCommon('MultipleSizes'),
  });

  return (
    <ProductCard
      preview={preview}
      productHref={slug}
      handleClick={handleClick}
      sellerUserName={sellerUsername}
      ProductAttributes={() => (
        <div className={styles.attributesContainer}>
          <Body variant="sm-regular" className={styles.brandName}>
            {brandName || tCommon('Other')}
          </Body>
          <Body variant="sm-regular">{sizeString}</Body>
          <Price
            currencyCode={displayPrice.headline_price.currency as CurrencyCode}
            discountedPrice={
              displayPrice.strikethrough_price
                ? displayPrice.headline_price.amount
                : undefined
            }
            price={
              displayPrice.strikethrough_price?.amount ??
              displayPrice.headline_price.amount
            }
          />
          {displayPrice.secondary_price && (
            <SecondaryPriceLine secondaryPrice={displayPrice.secondary_price} />
          )}
        </div>
      )}
      Overlay={() => (
        <ProductCardLike
          productId={id}
          isLiked={isLiked}
          guestLikeProduct={guestLikeProduct}
        />
      )}
      {...(hoverPicture && {
        HoverOverlay: () => (
          <LazyLoadImage
            className={styles.hoverImage}
            {...buildProductImageProps({
              preview: hoverPicture,
              sellerUserName: sellerUsername,
            })}
          />
        ),
      })}
    />
  );
}
